import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, filter, firstValueFrom, map, switchMap } from 'rxjs';

import { CipAuthHelperService } from './internal/cip-auth-helper.service';
import { CIP_CONFIG_ID } from './internal/cip-auth.config';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    #oidcService = inject(OidcSecurityService);
    #authHelperService = inject(CipAuthHelperService);
    #window = inject(DOCUMENT).defaultView as Window;

    /**
     * Returns the user data either from the token or by calling the user info endpoint if autoUserInfo is true in the config.
     * @param options To specify the configId
     */
    getUserData<T extends UserData>(options: CipOptions & { forceRefresh?: boolean } = {}): Observable<T> {
        const configId = options.configId ?? CIP_CONFIG_ID;
        return options.forceRefresh
            ? this.#oidcService.forceRefreshSession({}, configId).pipe(map(response => response.userData))
            : this.#oidcService.userData$.pipe(
                  map(userData => userData.allUserData.find(u => u.configId === CIP_CONFIG_ID)?.userData),
                  filter(Boolean)
              );
    }

    /**
     * Login the user
     * @param options the custom params for the login
     */
    login(options: Partial<CipAuthenticationOptions> = {}): void {
        const configId = options.configId ?? CIP_CONFIG_ID;
        const authOptions = { customParams: options.customParams, redirectUrl: options.redirectUrl };
        this.#oidcService.authorize(configId, authOptions);
    }

    /**
     * Logout the user
     * @param options To specify the configId
     * @see CipLogoutRequestOptions
     */
    logout(options: Partial<CipAuthenticationOptions> = {}): Promise<void> {
        const configId = options.configId ?? CIP_CONFIG_ID;
        const authOptions = {
            customParams: options.customParams,
            urlHandler: toUrlHandler(this.#window.location, options.redirectUrl)
        };
        return firstValueFrom(this.#oidcService.logoff(configId, authOptions).pipe(map(() => sessionStorage?.clear())));
    }

    /**
     * Check if the user is authenticated
     */
    isAuthenticated(options: CipOptions = {}): Observable<boolean> {
        const configId = options.configId ?? CIP_CONFIG_ID;
        return this.#oidcService.isAuthenticated(configId);
    }

    /**
     * Get the access token and optional perform an automatic login if not authenticated
     * @param options To specify the configId and if autoLogin should be performed
     */
    getAccessToken(options: CipOptions & { autoLogin?: boolean } = {}): Observable<string> {
        const configId = options.configId ?? CIP_CONFIG_ID;
        if (options.autoLogin) {
            return this.isAuthenticated(options).pipe(
                this.#authHelperService.processAuthentication(configId),
                switchMap(() => this.#oidcService.getAccessToken(configId))
            );
        } else {
            return this.#oidcService.getAccessToken(configId);
        }
    }
}

function toUrlHandler(location: Location, redirectUrl?: string): undefined | ((url: string) => void) {
    if (!redirectUrl) {
        return undefined;
    }
    return logoutUrl => {
        const url = new URL(logoutUrl);
        url.searchParams.set('post_logout_redirect_uri', redirectUrl);
        location.href = url.toString();
    };
}

export interface CipAuthenticationOptions extends CipOptions {
    redirectUrl: string;
    customParams: Record<string, string | number | boolean>;
}

interface CipOptions {
    configId?: string;
}

export interface UserData {
    preferred_username: CipUserId;
    communication_relation: 'B2B' | 'B2C' | 'B2E';
    email: string;
    impersonator?: {
        username: CipUserId;
    };
    name: string;
    [key: string]: unknown;
}

export type CipUserId =
    // b2b & b2e
    | `${'u' | 'z'}${number}${number}${number}${number}${number}${number}`
    // b2c
    | `${'m' | 'c'}${number}${number}${number}${number}${number}${number}${number}`;
